import React, { FC, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { AxiosRequestConfig } from 'axios';
import { ReviewBriefRequest } from './constants';
import axiosInstant from '../../api';
import { editProjectBrief } from '../../api/endpoints';
import { BrandProject } from '../../pages/videoProduction/components/constants';
import { BriefView } from './BriefView';
import { Button } from '../Button/Button';
import { BriefDenialDialog } from './BriefDenialDialog';
import { useProcessSiblings } from '../../hooks/useProcessSiblings';
import { resolveProjectBrandId } from '../../utils';
import { SharedBriefNotice } from './SharedBriefNotice';

interface Props {
  readonly?: boolean;
  project: BrandProject;
  onClose: () => void;
  onComplete: (isAccepted: boolean) => void;
}

export const BriefReviewDialog: FC<Props> = ({
  project,
  onClose,
  readonly,
  onComplete,
}) => {
  const [showDenialDialog, setShowDenialDialog] = useState(false);
  const [statusPayload, setStatusPayload] = useState<ReviewBriefRequest>();
  const [submitting, setSubmitting] = useState(false);

  const sharedProcess = useProcessSiblings(
    project?.order_id != null ? Number(project.order_id) : undefined,
    project?.id,
    { siblingCount: project?.process_project_count }
  );
  const otherProcessProjects = sharedProcess.siblingProjects.filter(
    (p) => Number(p.id) !== Number(project?.id)
  );

  const { id, briefs } = project;
  const brandId = resolveProjectBrandId(project);

  if (!brandId || !id || !briefs?.length) return null;

  const updateBriefStatus = async (data: ReviewBriefRequest) => {
    if (readonly) return;
    setStatusPayload(data);
    setSubmitting(true);

    try {
      const payload: AxiosRequestConfig = {
        method: 'PATCH',
        url: editProjectBrief(brandId, id, briefs?.[0].id),
      };
      const obj = {
        status: data.brief_approved ? 'Brief Approved' : 'Brief Denied',
        denied_reason: data.denied_reason,
      };
      await axiosInstant({ ...payload, data: obj });

      toast.success(
        `Brief ${data.brief_approved ? 'approved' : 'denied'} successfully.`
      );
      setSubmitting(false);
      onComplete(data.brief_approved);
    } catch (err) {
      setSubmitting(false);
      toast.error((err as Error)?.message);
    }
  };

  if (showDenialDialog) {
    return (
      <BriefDenialDialog
        onCancel={() => setShowDenialDialog(false)}
        onOkay={(denied_reason) =>
          updateBriefStatus({ brief_approved: false, denied_reason })
        }
      />
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: { maxWidth: 814 } }}
    >
      <DialogTitle>
        <Typography variant="h5">
          Project Brief
        </Typography>
      </DialogTitle>
      <DialogContent className="border-y border-border-token">
        {sharedProcess.isShared && (
          <Box className="pt-5 pb-6">
            <SharedBriefNotice
              siblings={otherProcessProjects}
              variant="review"
            />
          </Box>
        )}
        <BriefView brief={briefs[0]} />
      </DialogContent>
      <DialogActions className="!px-6 !py-4">
        {readonly && (
          <Button
            variant="contained"
            className="h-[40px] flex items-center !py-0 !px-4 !min-w-fit"
          >
            Download PDF
          </Button>
        )}
        {!readonly && (
          <Box className="flex items-center gap-6">
            <Button
              disabled={submitting}
              variant="contained"
              onClick={() => setShowDenialDialog(true)}
              className="h-[40px] flex items-center !py-0 !px-4 !min-w-fit !bg-[#EB3E33] hover:!bg-[#d82d21]"
            >
              {statusPayload?.brief_approved === false ? 'Denying...' : 'Deny'}
            </Button>
            <Button
              disabled={submitting}
              variant="contained"
              onClick={() => updateBriefStatus({ brief_approved: true })}
              className="h-[40px] flex items-center !py-0 !px-4 !min-w-fit !bg-[#1FCE7A] hover:!bg-[#45ae7b]"
            >
              {statusPayload?.brief_approved === true
                ? 'Approving...'
                : 'Approve'}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};
